import { useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './assets/css/style.css';
import './assets/css/grid.css';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import PageContainer from './components/PageContainer';
import { DEBUG, SITE_TITLE, TIMEZONE } from './config';
import { StateProvider, useAppState } from './state/StateContext';
import { __, isFullAppUrl } from './functions/commom';
import Search from './components/Search';
import { getInsurancePlans } from './functions/insuranceSearchService';
import InsuranceForm from './components/InsuranceForm';
import LoadingScreen from './components/LoadingScreen';
import moment from 'moment';

// Layout component that includes Header and Footer
const Layout = () => {
  return (
    <>
      {isFullAppUrl() && <Header />}
      {isFullAppUrl() ? (
        <PageContainer>
          <Outlet />
        </PageContainer>
      ) : (
        <Outlet />
      )}
      {isFullAppUrl() && <Footer />}
    </>
  );
};

// Create a wrapper component to initialize insurance state
const AppWrapper = () => {
  const state = useAppState();

  // Initialize Timezone
  moment.tz.setDefault(TIMEZONE);

  // Debug display for state
  const debugDisplay = DEBUG && (
    <div
      style={{
        padding: '10px',
        backgroundColor: '#f5f5f5',
        margin: '10px',
        borderRadius: '4px',
      }}
    >
      <div>State data:</div>
      <pre>{JSON.stringify(state.get(), null, 2)}</pre>
    </div>
  );

  // Load insurance list data when the application starts
  const loadInsuranceList = useCallback(async () => {
    // Check if insurancePlans exists and has items
    const currentPlans = state?.insurancePlans?.get();

    if (
      !currentPlans ||
      (Array.isArray(currentPlans) && currentPlans.length === 0)
    ) {
      try {
        state.loading.set(true);
        const insurancePlans = await getInsurancePlans();
        state.insurancePlans.set(insurancePlans);
        state.loading.set(false);
      } catch (error) {
        console.error('Error fetching insurance plans:', error);
        state.loading.set(false);
      }
    }
  }, [state.insurancePlans, state.loading]);

  // Call loadInsuranceList once on component mount
  useEffect(() => {
    loadInsuranceList();
  }, [loadInsuranceList]);

  useEffect(() => {
    // changing browser title when state topbar title changes
    if (isFullAppUrl()) {
      document.title =
        `${__(state.topbar.title.get())} - ${__(SITE_TITLE)}` || __(SITE_TITLE);
    }
  }, [state.topbar.title]);

  return (
    <>
      {state.loading.get() === true && <LoadingScreen />}
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Search />} />
            <Route path="insurance/:category" element={<Search />} />
            <Route path="insurance/:category/:id" element={<Search />} />
            <Route
              path="insurance/:category/:id/:tier"
              element={<InsuranceForm />}
            />
            <Route path="*" element={<Search />} />
          </Route>
        </Routes>
      </BrowserRouter>
      {debugDisplay}
    </>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <StateProvider>
    <AppWrapper />
  </StateProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
